import React, { useState } from "react";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";
import { ReactComponent as MoreHorizIcon } from "../../../../assets/images/menukb.svg";
import { COLORS, CONTENT_MAX_WIDTH, FONT, cardTitleSx } from "../shared/styles";
import { PrivateIcon, SharedIcon } from "./icons";
import {
  formatShortcut,
  getGroupsLabel,
  humanizeTag,
} from "./cannedUtils";

function StatusChip({ isPrivate }) {
  if (isPrivate) {
    return (
      <Box
        component="span"
        sx={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          gap: "4px",
          width: 88,
          height: 24,
          borderRadius: "12px",
          bgcolor: COLORS.white,
          border: `1px solid ${COLORS.border}`,
          color: COLORS.textPrimary,
          fontFamily: FONT.family,
          fontSize: "12px",
          fontWeight: 500,
          lineHeight: "22px",
        }}
      >
        <PrivateIcon />
        Private
      </Box>
    );
  }
  return (
    <Box
      component="span"
      sx={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "4px",
        width: 88,
        height: 24,
        borderRadius: "12px",
        bgcolor: "#F3FBFF",
        color: COLORS.primary,
        fontFamily: FONT.family,
        fontSize: "12px",
        fontWeight: 500,
        lineHeight: "22px",
      }}
    >
      <SharedIcon />
      Shared
    </Box>
  );
}

function CannedCard({ item, groupMap, onEdit, onDelete }) {
  const [anchorEl, setAnchorEl] = useState(null);
  const title = item.title || humanizeTag(item.shortcut || item?.tagDetails?.tag);
  const shortcut = formatShortcut(item.shortcut || item?.tagDetails?.tag);
  const groupsLabel = getGroupsLabel(item, groupMap);
  const createdBy = item?.userDetails?.full_name || "Unknown";

  return (
    <Box
      component="article"
      sx={{
        width: "100%",
        maxWidth: CONTENT_MAX_WIDTH,
        minHeight: 176,
        p: "16px",
        borderRadius: "8px",
        border: `1px solid ${COLORS.border}`,
        bgcolor: COLORS.white,
        boxSizing: "border-box",
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: "12px",
        }}
      >
        <Typography component="p" sx={cardTitleSx}>
          {title}
        </Typography>
        <Box sx={{ display: "flex", alignItems: "center", gap: "4px", flexShrink: 0 }}>
          <StatusChip isPrivate={item.is_private} />
          <IconButton
            size="small"
            onClick={(e) => setAnchorEl(e.currentTarget)}
            sx={{ p: 0.5 }}
          >
            <MoreHorizIcon />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "12px",
          flexWrap: "wrap",
          mt: "8px",
        }}
      >
        <Box
          component="span"
          sx={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            px: "8px",
            py: "2px",
            borderRadius: "4px",
            bgcolor: "#F1F3F5",
            color: COLORS.textPrimary,
            fontFamily: FONT.family,
            fontSize: "12px",
            fontWeight: 500,
            lineHeight: "18px",
          }}
        >
          {shortcut}
        </Box>
        {groupsLabel ? (
          <Typography
            component="span"
            sx={{
              color: COLORS.textSecondary,
              fontFamily: FONT.family,
              fontSize: "12px",
              fontWeight: 400,
              lineHeight: "18px",
            }}
          >
            {groupsLabel}
          </Typography>
        ) : null}
      </Box>

      <Box
        sx={{
          width: "100%",
          maxWidth: 600,
          mt: "12px",
          p: "12px",
          borderRadius: "8px",
          bgcolor: COLORS.backgroundLight,
          color: COLORS.textSecondary,
          fontFamily: FONT.family,
          fontSize: "14px",
          fontWeight: 400,
          lineHeight: "normal",
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
        }}
      >
        {item.content}
      </Box>

      <Typography
        component="p"
        sx={{
          m: "12px 0 0",
          color: COLORS.textSecondary,
          fontFamily: FONT.family,
          fontSize: "12px",
          fontWeight: 400,
          lineHeight: "18px",
        }}
      >
        Created by {createdBy}
      </Typography>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        PaperProps={{
          sx: {
            width: 184,
            borderRadius: "8px",
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 15px 20px 0 rgba(6, 21, 34, 0.05)",
          },
        }}
      >
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onEdit(item);
          }}
          sx={{ color: COLORS.textPrimary, fontFamily: FONT.family, fontSize: 14 }}
        >
          Edit
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchorEl(null);
            onDelete(item);
          }}
          sx={{ color: "#FF4D4F !important", fontFamily: FONT.family, fontSize: 14 }}
        >
          Delete
        </MenuItem>
      </Menu>
    </Box>
  );
}

export default CannedCard;
